/**
 * Clients API calls used by the CogesKey portal.
 *
 * IMPORTANT: the Clients API is shared with the mobile app and other services.
 * Paths, query strings and payloads below mirror the Vue 2 portal 1:1 — do not
 * "clean them up" without a matching, backwards-compatible API change.
 */
import { clientsApi } from '@/http/clientsApi';
import { spaConfig } from '@/config/spaConfig';
import type { BlogPost } from '@/models/blog/blogPost';
import type { BlogPostSummary } from '@/models/blog/blogPostSummary';
import type { Country, State } from '@/models/countries/country-state';
import type { MerchantConfig } from '@/models/config/merchantConfig';
import type { MerchantPrivacy } from '@/models/config/merchantPrivacy';
import type { PrivacyConsents } from '@/models/customers/privacyConsents';
import type { Media } from '@/models/medias/media';
import type { MediaStatus } from '@/models/medias/mediaStatus';
import type { WebPaymentConfig } from '@/models/web-payments/webPaymentConfig';
import type { WebPaymentOrder } from '@/models/web-payments/webPaymentOrder';
import type { WebPaymentService } from '@/models/web-payments/webPaymentService';

const seg = encodeURIComponent;

/* ----------------------------------------------------------------- blog -- */

export const blogApi = {
  async list(lang: string, page = 1): Promise<BlogPostSummary[]> {
    const { data } = await clientsApi.get<BlogPostSummary[]>('blog/posts', { params: { lang, page } });
    return data;
  },
  /** Same call as list(), plus the raw `X-Pagination` header ({ currentPage, totalPages, size }). */
  async listPage(lang: string, page = 1): Promise<{ items: BlogPostSummary[]; paginationHeader?: string }> {
    const response = await clientsApi.get<BlogPostSummary[]>('blog/posts', { params: { lang, page } });
    const header = response.headers['x-pagination'];
    return { items: response.data, paginationHeader: typeof header === 'string' ? header : undefined };
  },
  async latest(lang: string): Promise<BlogPostSummary[]> {
    const { data } = await clientsApi.get<BlogPostSummary[]>('blog/posts/latest', { params: { lang } });
    return data;
  },
  async get(id: string, lang: string): Promise<BlogPost> {
    const { data } = await clientsApi.get<BlogPost>(`blog/posts/${seg(id)}`, { params: { lang } });
    return data;
  },
};

/* ------------------------------------------------------------ countries -- */

export const countriesApi = {
  async countries(lang: string): Promise<Country[]> {
    const { data } = await clientsApi.get<Country[]>('countries/', { params: { lang } });
    return data;
  },
  async states(country: string): Promise<State[]> {
    const { data } = await clientsApi.get<State[]>(`countries/${seg(country)}/states`);
    return data;
  },
};

/* --------------------------------------------------------------- config -- */

export const merchantConfigApi = {
  async get(merchantId: string): Promise<MerchantConfig> {
    const { data } = await clientsApi.get<MerchantConfig>(`config/${seg(merchantId)}`);
    return data;
  },
  async privacy(merchantId: string, lang: string): Promise<MerchantPrivacy> {
    const { data } = await clientsApi.get<MerchantPrivacy>(`config/${seg(merchantId)}/privacy`, {
      params: { lang },
    });
    return data;
  },
  /** QR (mykey) landing: merchant config by media hash. */
  async byMediaHash(hash: string): Promise<MerchantConfig> {
    const { data } = await clientsApi.get<MerchantConfig>(`config/media/${seg(hash)}`);
    return data;
  },
  /** Mobile app registration link: merchant config by mobile hash. */
  async byMobileHash(hash: string): Promise<MerchantConfig> {
    const { data } = await clientsApi.get<MerchantConfig>(`config/mobile/${seg(hash)}`);
    return data;
  },
};

/* ------------------------------------------------------------ customers -- */

export interface CustomerProfile {
  id?: string;
  email?: string;
  name?: string;
  surname?: string;
  language?: string;
  gender?: string;
  dateOfBirth?: string;
  country?: string;
  state?: string;
  streetAddress?: string;
  city?: string;
  postalCode?: string;
  [key: string]: unknown;
}

/** POST customers/register (QR / web registration). */
export interface CustomerRegistrationRequest {
  name: string;
  surname: string;
  email: string;
  password: string;
  confirmPassword: string;
  privacy: boolean;
  privacy1: boolean;
  privacy2: boolean;
  privacy3: boolean;
  mediaHash?: string;
  merchantId: string;
  mediaType: number;
  mediaHwId: string;
  mediaSerial: string;
  language: string;
}

export const customersApi = {
  async me(): Promise<CustomerProfile> {
    const { data } = await clientsApi.get<CustomerProfile>('customers/me');
    return data;
  },
  async update(profile: CustomerProfile): Promise<void> {
    await clientsApi.put('customers/me', profile);
  },
  async register(request: CustomerRegistrationRequest): Promise<void> {
    await clientsApi.post('customers/register', request);
  },
  async remove(): Promise<void> {
    await clientsApi.delete('customers/me');
  },
  async privacyConsents(merchantId: string): Promise<PrivacyConsents> {
    const { data } = await clientsApi.get<PrivacyConsents>(`customers/${seg(merchantId)}/privacy`);
    return data;
  },
  async updatePrivacyConsents(merchantId: string, consents: Partial<PrivacyConsents>): Promise<void> {
    await clientsApi.put(`customers/${seg(merchantId)}/privacy`, consents);
  },
};

/* --------------------------------------------------------------- medias -- */

export const mediasApi = {
  async byHash(hash: string): Promise<MediaStatus> {
    const { data } = await clientsApi.get<MediaStatus>(`medias/hash/${seg(hash)}`);
    return data;
  },
  async list(): Promise<Media[]> {
    const { data } = await clientsApi.get<Media[]>('medias', { params: { cogesKeyEnabledOnly: true } });
    return data;
  },
  async rename(mediaId: string, merchantId: string, alias: string): Promise<void> {
    await clientsApi.put(`medias/${seg(mediaId)}`, { merchantId, alias });
  },
  async associate(hash: string): Promise<void> {
    await clientsApi.post(`medias/${seg(hash)}/association`);
  },
  async dissociate(mediaId: string): Promise<void> {
    await clientsApi.delete(`medias/${seg(mediaId)}/association`);
  },
  // Kept as page/size query params: the API contract expects them there.
  async transactions(mediaId: string, merchantId: string, page = 1, size = 10) {
    return clientsApi.get(`medias/${seg(mediaId)}/transactions`, { params: { merchantId, page, size } });
  },
};

/* ----------------------------------------------------------- mobile app -- */

export interface MobileAppRegistrationRequest {
  email: string;
  password: string;
  name: string;
  surname: string;
  language: string;
  privacy: boolean;
  merchantId: string;
  mobileHash: string;
  privacy1?: boolean;
  privacy2?: boolean;
  privacy3?: boolean;
}

export interface SendOtpResponse {
  success: boolean;
  expiresInSeconds?: number;
  error?: string;
}

export interface VerifyOtpResponse {
  verified: boolean;
  error?: string;
}

export const mobileAppApi = {
  async register(request: MobileAppRegistrationRequest): Promise<void> {
    await clientsApi.post('mobile/customer/register', request);
  },
  async sendOtp(email: string, language: string): Promise<SendOtpResponse> {
    const { data } = await clientsApi.post<SendOtpResponse>('mobile/email-verification/send', { email, language });
    return data;
  },
  async verifyOtp(email: string, code: string): Promise<VerifyOtpResponse> {
    const { data } = await clientsApi.post<VerifyOtpResponse>('mobile/email-verification/verify', { email, code });
    return data;
  },
};

/* --------------------------------------------------------- web payments -- */

export const webPaymentsApi = {
  async services(): Promise<WebPaymentService[]> {
    const { data } = await clientsApi.get<WebPaymentService[]>('webpaymentservices');
    return data;
  },
  async configs(merchantId: string): Promise<WebPaymentConfig[]> {
    const { data } = await clientsApi.get<WebPaymentConfig[]>(`webpaymentconfigs/${seg(merchantId)}`);
    return data;
  },
  /** Response shape depends on the provider (Nexi: { requestDetail }, Assiopay: { amount }). */
  async createOrder(order: Partial<WebPaymentOrder>): Promise<any> {
    const { data } = await clientsApi.post('webpaymentorder', order);
    return data;
  },
  /** Absolute IPN URL Nexi posts the payment outcome to (server-side crediting). */
  ipnEndpoint(urlpost: string): string {
    return `${spaConfig().clientsApiUrl}/${urlpost}`;
  },
};

/* --------------------------------------------------------------- vendon -- */

export const vendonApi = {
  async payment(merchantId: string, paymentId: string, webOrderId: string): Promise<any> {
    const { data } = await clientsApi.get(`${seg(merchantId)}/vendongateway/payment/${seg(paymentId)}`, {
      params: { webOrderId },
    });
    return data;
  },
};
